/**
 * HTTP plumbing shared by the payments-runtime config surfaces (`payment_fees`, `provider_rail_routes`).
 *
 * Both endpoints are paginated the same way by coloca-nest's `PaginationDto`, so the response shape, the page
 * ceiling and the query serializer live here instead of being restated per service — a drift between them
 * would show up as a silently truncated list or a 400.
 */

/** Shape of every paginated coloca-nest list (`paginate()` in `src/common/dto`). */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

/** Backend `PaginationDto.Max(200)`. Requesting more is a 400, not a silent clamp. */
export const PAYMENTS_CONFIG_MAX_LIMIT = 200;

/**
 * Query string from a flat param bag, skipping `undefined` and `""` so an unset filter is absent rather than
 * sent empty (an empty `country` would fail `@Length(3, 3)`).
 *
 * Booleans go through `String(value)` on purpose: the routes and fees query DTOs coerce **only** the exact
 * literals `"true"` / `"false"`, and reject anything else instead of guessing — `?is_active=1` is a 400.
 */
export function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}
